import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Helmet } from "react-helmet";
import { MdGroup } from "react-icons/md";
import { GiWhistle } from "react-icons/gi";

import { EVENT_DETAIL_PUBLIC, GAME_BROADCASTS, LIVE_GAME_TIMELINE } from "../../components/game/query/event";
import { GET_SOCCER_GAME_TIMELINE } from "../../components/game/query/GetGameTimelineData";
import TopBar from "../../components/common/public/TopBar";
import PublicFooter from "../../components/common/public/Footer";
import GameVideoPlayer from "../../components/event/GameVideoPlayer";
import { HOST_URI } from "../../constants/vars";
import InitialsAvatar from "../../util/name_initial";
import { formattedDateTime } from "../../util/date";
import { GlobalStyles } from "../../constants/styles";
import DownloadApp from "../../components/common/public/DownloadApp";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const { colors } = GlobalStyles;
const pageBg = colors?.woodsmoke || "#14151a";
const surface = colors?.shark || "#1f1f2e";
const accent = colors?.canary || "var(--kbs-primary, #00adff)";
const textMain = colors?.white || "#ffffff";
const textMuted = colors?.manatee || "#8d929d";

function getTimelineEventConfig(t) {
    return {
        goal: {
            icon: "fas fa-futbol",
            accent: "#20c997",
            label: t("gameElement.soccerGameSheet.eventTypes.goal", {}, "Goal"),
            shortLabel: "GOAL",
        },
        assist: {
            icon: "fas fa-hands-helping",
            accent: "#4dabf7",
            label: t("gameElement.soccerGameSheet.eventTypes.assist", {}, "Assist"),
            shortLabel: "ASSIST",
        },
        substitution_in: {
            icon: "fas fa-arrow-up",
            accent: "#51cf66",
            label: t("gameElement.soccerGameSheet.eventTypes.substitution_in", {}, "Subbed In"),
            shortLabel: "IN",
        },
        substitution_out: {
            icon: "fas fa-arrow-down",
            accent: "#ff6b6b",
            label: t("gameElement.soccerGameSheet.eventTypes.substitution_out", {}, "Subbed Out"),
            shortLabel: "SUB",
        },
        yellow_card: {
            icon: "fas fa-square",
            accent: "#ffd43b",
            label: t("gameElement.soccerGameSheet.eventTypes.yellow_card", {}, "Yellow Card"),
            shortLabel: "YC",
        },
        red_card: {
            icon: "fas fa-square",
            accent: "#fa5252",
            label: t("gameElement.soccerGameSheet.eventTypes.red_card", {}, "Red Card"),
            shortLabel: "RC",
        },
        own_goal: {
            icon: "fas fa-futbol",
            accent: "#f06595",
            label: t("gameElement.soccerGameSheet.eventTypes.own_goal", {}, "Own Goal"),
            shortLabel: "OG",
        },
    };
}

function resolvePublicImageUrl(url) {
    if (!url || typeof url !== "string") return null;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/")) return `${HOST_URI}${url}`;
    if (url.startsWith("media/")) return `${HOST_URI}/${url}`;
    return `${HOST_URI}/media/${url}`;
}

function normalizeEventPeople(people) {
    if (!Array.isArray(people)) return [];

    return people
        .map((person, index) => {
            const fullName = person?.fullName || person?.name || String(person || "");

            return {
                id: person?.id || `${fullName}-${index}`,
                fullName,
            };
        })
        .filter((person) => person.fullName);
}

function PublicEventPage() {
    const { t, language } = useTranslation();
    const { eventId } = useParams();
    const { data, loading, error, startPolling, stopPolling } = useQuery(EVENT_DETAIL_PUBLIC, {
        variables: { eventId },
    });
    const gameId = data?.eventDetail?.game?.id;
    const { data: broadcastData } = useQuery(GAME_BROADCASTS, {
        variables: { gameId: Number(gameId) },
        skip: !gameId,
        pollInterval: 10000,
        fetchPolicy: "network-only",
    });
    const streamingEnabled = broadcastData?.streamingEnabled === true;
    const gameBroadcasts = broadcastData?.gameBroadcasts || [];
    const liveBroadcast = gameBroadcasts.find((broadcast) => broadcast.status === "live");
    const { data: liveTimelineData } = useQuery(LIVE_GAME_TIMELINE, {
        variables: {
            gameId: Number(gameId),
        },
        skip: !gameId,
        pollInterval: liveBroadcast?.status === "live" ? 5000 : 0,
        fetchPolicy: "network-only",
    });
    const latestGameEvent = useMemo(() => {
        const events = [
            ...(liveTimelineData?.soccerPlayerScoresByGame || []),
            ...(liveTimelineData?.hockeyPlayerScoresByGame || []),
        ];
        return events.sort((first, second) => {
            const firstMinute = Number(first?.minute ?? 0);
            const secondMinute = Number(second?.minute ?? 0);
            if (secondMinute !== firstMinute) return secondMinute - firstMinute;
            return Number(second?.id ?? 0) - Number(first?.id ?? 0);
        })[0] || null;
    }, [liveTimelineData]);

    useEffect(() => {
        if (liveBroadcast?.status === "live") {
            startPolling(5000);
        } else {
            stopPolling();
        }
        return () => stopPolling();
    }, [liveBroadcast?.status, startPolling, stopPolling]);

    const [activeTab, setActiveTab] = useState("timeline");
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);

    useEffect(() => {
        if (loading) {
            document.title = `AngeonX Sports | ${t("publicEventPage.meta.loadingTitle")}`;
        } else if (error || !data?.eventDetail) {
            document.title = `AngeonX Sports | ${t("publicEventPage.meta.notFoundTitle")}`;
        }
    }, [loading, error, data, t]);

    if (loading) {
        return (
            <div
                className="kbs-page kbs-page--public-event d-flex flex-column"
                style={{ backgroundColor: pageBg, minHeight: "100vh", color: textMain }}
            >
                <TopBar imgPath="/dist/img/angeonx-sports.png" />
                <div className="container flex-grow-1 d-flex align-items-center justify-content-center">
                    <div className="kbs-loading text-center">
                        <div className="spinner-border text-light mb-3" role="status" />
                        <div className="kbs-text-muted">{t("publicEventPage.loading.details")}</div>
                    </div>
                </div>
                <PublicFooter imgPath="/dist/img/" />
            </div>
        );
    }

    if (error || !data?.eventDetail) {
        return (
            <div
                className="kbs-page kbs-page--public-event d-flex flex-column"
                style={{ backgroundColor: pageBg, minHeight: "100vh", color: textMain }}
            >
                <TopBar imgPath="/dist/img/angeonx-sports.png" />
                <div className="container flex-grow-1 d-flex align-items-center justify-content-center">
                    <div
                        className="kbs-card kbs-card--soft text-center p-4"
                        style={{ backgroundColor: surface, borderRadius: 16 }}
                    >
                        <h2 className="mb-2" style={{ color: accent }}>
                            {t("publicEventPage.error.title")}
                        </h2>
                        <p className="mb-0" style={{ color: textMuted }}>
                            {t("publicEventPage.error.description")}
                        </p>
                    </div>
                </div>
                <PublicFooter imgPath="/dist/img/" />
            </div>
        );
    }

    const event = data.eventDetail;
    const publicPhotos = event.publicPhotos || [];
    const publicPhotoSlides = publicPhotos
        .map((photo) => {
            const imageUrl = resolvePublicImageUrl(photo.imageUrl || photo.thumbnailUrl);
            const thumbnailUrl = resolvePublicImageUrl(photo.thumbnailUrl || photo.imageUrl);

            return {
                ...photo,
                imageUrl,
                thumbnailUrl,
            };
        })
        .filter((photo) => photo.imageUrl && photo.thumbnailUrl);
    const eventManagers = normalizeEventPeople(event.managers);
    const eventResponsibles = normalizeEventPeople(event.responsibles);

    const eventTitle =
        event.title ||
        (event.game
            ? `${event.game.homeTeam?.name || event.game.homeTeamAlias} vs ${
                event.game.visitorTeam?.name || event.game.visitorTeamAlias
            }`
            : t("publicEventPage.meta.eventFallback"));

    const eventDate = event.date ? formattedDateTime(event.date, event.timezone, language) : "";

    let latitude = null;
    let longitude = null;
    let address = null;

    if (event.eventType === "GENERIC") {
        latitude = event.genericEvent?.latitude;
        longitude = event.genericEvent?.longitude;
        address = event.genericEvent?.address;
    } else {
        latitude = event.field ? event.field.sportFacility?.latitude : event.latitude;
        longitude = event.field ? event.field.sportFacility?.longitude : event.longitude;
        address = event.field ? event.field.sportFacility?.address : event.address;
    }

    const orgName = event.season?.division?.organization?.name;
    const divisionName = event.season?.division?.name;
    const seasonName = event.season?.name;

    const competitionLine = [orgName, divisionName, seasonName]
        .filter(Boolean)
        .join(" • ");

    const status = (event.status || "SCHEDULED").toUpperCase();
    const statusStyles = (() => {
        switch (status) {
            case "COMPLETED":
                return {
                    bg: accent,
                    text: pageBg,
                    label: t("publicEventPage.status.final"),
                };
            case "IN_PROGRESS":
                return {
                    bg: colors?.sulu || "var(--kbs-secondary, #006CA8)",
                    text: pageBg,
                    label: t("publicEventPage.status.live"),
                };
            case "CANCELLED":
                return {
                    bg: "#ff6b6b",
                    text: pageBg,
                    label: t("publicEventPage.status.cancelled"),
                };
            default:
                return {
                    bg: textMuted,
                    text: textMain,
                    label: t("publicEventPage.status.upcoming"),
                };
        }
    })();

    const translatedStatus = (() => {
        switch (status) {
            case "COMPLETED":
                return t("publicEventPage.status.completed");
            case "IN_PROGRESS":
                return t("publicEventPage.status.inProgress");
            case "CANCELLED":
                return t("publicEventPage.status.cancelled");
            default:
                return t("publicEventPage.status.scheduled");
        }
    })();

    const isGame = !!event.game;

    const homeName = isGame
        ? event.game.homeTeam?.name || event.game.homeTeamAlias || t("publicEventPage.labels.teamFallback")
        : null;
    const visitorName = isGame
        ? event.game.visitorTeam?.name || event.game.visitorTeamAlias || t("publicEventPage.labels.teamFallback")
        : null;

    const metaTitleBase =
        isGame && homeName && visitorName ? `${homeName} vs ${visitorName}` : eventTitle;

    const titleParts = [metaTitleBase, competitionLine].filter(Boolean);
    const pageTitle = titleParts.length
        ? `${titleParts.join(" • ")} | ${t("publicEventPage.meta.siteName")}`
        : `${eventTitle} | ${t("publicEventPage.meta.siteName")}`;

    const description =
        [eventDate || null, address || null, competitionLine || null]
            .filter(Boolean)
            .join(" • ") || t("publicEventPage.meta.defaultDescription");

    const ogUrl =
        typeof window !== "undefined"
            ? `${window.location.origin}/events/${eventId}`
            : `${process.env.REACT_APP_WEB_HOST || "https://kbolasport.com"}/events/${eventId}`;

    const logoPath =
        (event.game?.homeTeam && event.game.homeTeam.logo) ||
        (event.game?.visitorTeam && event.game.visitorTeam.logo) ||
        null;

    const ogImage = logoPath
        ? `${HOST_URI}/media/${logoPath}`
        : "https://kbolasport.com/dist/img/angeonx-sports.png";

    const eventSchema = {
        "@context": "https://schema.org",
        "@type": "SportsEvent",
        name: metaTitleBase,
        description,
        startDate: event.date,
        eventStatus:
            status === "COMPLETED"
                ? "https://schema.org/EventCompleted"
                : status === "CANCELLED"
                    ? "https://schema.org/EventCancelled"
                    : "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        url: ogUrl,
        image: [ogImage],
        location: address
            ? {
                "@type": "Place",
                name: address,
            }
            : undefined,
        organizer: orgName
            ? {
                "@type": "SportsOrganization",
                name: orgName,
            }
            : undefined,
        homeTeam: homeName
            ? {
                "@type": "SportsTeam",
                name: homeName,
            }
            : undefined,
        awayTeam: visitorName
            ? {
                "@type": "SportsTeam",
                name: visitorName,
            }
            : undefined,
    };

    return (
        <div
            className="kbs-page kbs-page--public-event d-flex flex-column"
            style={{
                backgroundColor: pageBg,
                color: textMain,
                fontFamily: "'Poppins', sans-serif",
                minHeight: "100vh",
            }}
        >
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={ogUrl} />

                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="event" />
                <meta property="og:url" content={ogUrl} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:site_name" content={t("publicEventPage.meta.siteName")} />
                <meta property="og:locale" content={t("publicEventPage.meta.locale")} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={ogImage} />

                <script type="application/ld+json">
                    {JSON.stringify(eventSchema)}
                </script>
            </Helmet>

            <TopBar imgPath="/dist/img/angeonx-sports.png" />

            <main className="flex-grow-1">
                <div
                    className="container kbs-public-event-container"
                    style={{ maxWidth: 1200, padding: "24px 16px 40px", margin: "0 auto" }}
                >
                    <header className="kbs-public-event-header mb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="kbs-breadcrumb">
                            <div
                                style={{
                                    fontSize: 12,
                                    color: textMuted,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                }}
                            >
                                <span style={{ opacity: 0.9 }}>{t("publicEventPage.meta.siteName")}</span>
                                <span style={{ opacity: 0.5 }}> / </span>
                                <span style={{ opacity: 0.9 }}>{t("publicEventPage.breadcrumb.events")}</span>
                                <span style={{ opacity: 0.5 }}> / </span>
                                <span style={{ opacity: 1 }}>{eventTitle}</span>
                            </div>
                        </div>

                        <span
                            className="kbs-status-pill"
                            style={{
                                backgroundColor: statusStyles.bg,
                                color: statusStyles.text,
                                borderRadius: 999,
                                padding: "6px 18px",
                                fontSize: 11,
                                letterSpacing: "0.16em",
                                fontWeight: 700,
                                textTransform: "uppercase",
                            }}
                        >
                            {translatedStatus}
                        </span>
                    </header>

                    <div className="row g-4">
                        <div className="col-12 col-lg-6 order-2 order-lg-2">
                            <h3
                                className="kbs-section-title mb-3"
                                style={{
                                    fontSize: "1rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.15em",
                                    color: textMuted,
                                }}
                            >
                                {t("publicEventPage.sections.event")}
                            </h3>

                            <section
                                className="kbs-card kbs-card--hero mb-4"
                                style={{
                                    borderRadius: 20,
                                    padding: "22px 22px 18px",
                                    background: GlobalStyles.colors.shark,
                                    boxShadow: "0 24px 70px rgba(0,0,0,0.75)",
                                }}
                            >
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
                                    <div>
                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: textMuted,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.16em",
                                                marginBottom: 4,
                                            }}
                                        >
                                            {event.eventType || t("publicEventPage.labels.eventTypeFallback")}
                                        </div>
                                        {competitionLine && (
                                            <div
                                                style={{
                                                    fontSize: 13,
                                                    color: textMuted,
                                                    marginTop: 2,
                                                }}
                                            >
                                                {competitionLine}
                                            </div>
                                        )}
                                    </div>

                                    <div className="text-md-right">
                                        {eventDate && (
                                            <div
                                                className="d-flex d-md-block align-items-center mb-1"
                                                style={{ fontSize: 13, color: textMain }}
                                            >
                                                <i
                                                    className="far fa-calendar-alt me-2"
                                                    style={{ color: accent, fontSize: 14, padding: 5 }}
                                                />
                                                <span>{eventDate}</span>
                                            </div>
                                        )}
                                        {address && (
                                            <div
                                                className="d-flex d-md-block align-items-center"
                                                style={{ fontSize: 13, color: textMain }}
                                            >
                                                <i
                                                    className="fas fa-map-marker-alt me-2"
                                                    style={{ color: accent, fontSize: 14, padding: 5 }}
                                                />
                                                <span>{address}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {event.game && (
                                    <div
                                        className="kbs-scoreboard mt-2"
                                        style={{
                                            borderRadius: 16,
                                            padding: "14px 16px",
                                            border: "1px solid rgba(255,255,255,0.04)",
                                        }}
                                    >
                                        <div className="d-none d-md-block">
                                            <div className="d-flex align-items-center" style={{ gap: 4 }}>
                                                <div style={{ flex: "1 1 0", minWidth: 0 }}>
                                                    <TeamBlock
                                                        team={event.game.homeTeam}
                                                        alias={event.game.homeTeamAlias}
                                                        label={t("publicEventPage.labels.home")}
                                                    />
                                                </div>
                                                <div style={{ flex: "0 0 auto", minWidth: 76, textAlign: "center" }}>
                                                    <div
                                                        style={{
                                                            fontSize: "clamp(26px, 3vw, 34px)",
                                                            fontWeight: 800,
                                                            letterSpacing: "0.04em",
                                                            color: textMain,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {event.game.scoreHome} - {event.game.scoreVisitor}
                                                    </div>
                                                    <div
                                                        style={{
                                                            fontSize: 11,
                                                            color: textMuted,
                                                            textTransform: "uppercase",
                                                            letterSpacing: "0.18em",
                                                            marginTop: 4,
                                                        }}
                                                    >
                                                        {statusStyles.label}
                                                    </div>
                                                </div>
                                                <div style={{ flex: "1 1 0", minWidth: 0 }}>
                                                    <TeamBlock
                                                        team={event.game.visitorTeam}
                                                        alias={event.game.visitorTeamAlias}
                                                        label={t("publicEventPage.labels.away")}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-block d-md-none">
                                            <div className="text-center mb-2">
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color: textMuted,
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.18em",
                                                    }}
                                                >
                                                    {statusStyles.label}
                                                </span>
                                            </div>

                                            <div className="d-flex flex-column gap-2">
                                                <MobileTeamRow
                                                    team={event.game.homeTeam}
                                                    alias={event.game.homeTeamAlias}
                                                    label={t("publicEventPage.labels.home")}
                                                    score={event.game.scoreHome}
                                                />
                                                <MobileTeamRow
                                                    team={event.game.visitorTeam}
                                                    alias={event.game.visitorTeamAlias}
                                                    label={t("publicEventPage.labels.away")}
                                                    score={event.game.scoreVisitor}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </section>

                            <section
                                className="kbs-card kbs-card--tabs"
                                style={{
                                    backgroundColor: surface,
                                    borderRadius: 18,
                                    paddingTop: 0,
                                }}
                            >
                                <div
                                    className="kbs-tabs-header d-flex"
                                    style={{
                                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                                        padding: "0 20px",
                                        marginTop: 6,
                                    }}
                                >
                                    {[
                                        {
                                            key: "timeline",
                                            label: t("publicEventPage.labels.timeline"),
                                            iconClass: "fas fa-stream",
                                        },
                                        {
                                            key: "lineups",
                                            label: t("publicEventPage.labels.lineups"),
                                            iconClass: "fas fa-users",
                                        },
                                        {
                                            key: "stats",
                                            label: t("publicEventPage.labels.stats"),
                                            iconClass: "fas fa-chart-bar",
                                        },
                                    ].map((tab) => {
                                        const isActive = activeTab === tab.key;
                                        return (
                                            <button
                                                key={tab.key}
                                                type="button"
                                                onClick={() => setActiveTab(tab.key)}
                                                className={`kbs-tab-btn ${
                                                    isActive ? "kbs-tab-btn--active" : ""
                                                }`}
                                                style={{
                                                    flex: 1,
                                                    padding: "12px 0",
                                                    border: "none",
                                                    background: "transparent",
                                                    cursor: "pointer",
                                                    fontSize: 13,
                                                    fontWeight: isActive ? 600 : 500,
                                                    color: isActive ? accent : textMuted,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.12em",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: 8,
                                                    borderBottom: isActive
                                                        ? `2px solid ${accent}`
                                                        : "2px solid transparent",
                                                }}
                                            >
                                                <i className={tab.iconClass} />
                                                <span>{tab.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div
                                    className="kbs-tabs-body"
                                    style={{ padding: "16px 20px 20px", minHeight: 140 }}
                                >
                                    {activeTab === "timeline" && (
                                        <TimelineTabContent
                                            event={event}
                                            description={event.description}
                                            accent={accent}
                                            textMain={textMain}
                                            textMuted={textMuted}
                                            t={t}
                                        />
                                    )}

                                    {activeTab === "lineups" && (
                                        <LineupsTabPlaceholder
                                            textMain={textMain}
                                            textMuted={textMuted}
                                            t={t}
                                        />
                                    )}

                                    {activeTab === "stats" && (
                                        <StatsTabPlaceholder
                                            textMain={textMain}
                                            textMuted={textMuted}
                                            t={t}
                                        />
                                    )}
                                </div>
                            </section>

                            {publicPhotoSlides.length > 0 && (
                                <section
                                    className="kbs-card kbs-card--photos mt-4"
                                    style={{
                                        backgroundColor: surface,
                                        borderRadius: 18,
                                        padding: "18px 20px 20px",
                                        boxShadow: "0 20px 50px rgba(0,0,0,0.65)",
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h3
                                            className="mb-0"
                                            style={{
                                                fontSize: "0.95rem",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.15em",
                                                color: textMuted,
                                            }}
                                        >
                                            {t("publicEventPage.sections.photos")}
                                        </h3>
                                        <span
                                            style={{
                                                borderRadius: 999,
                                                padding: "4px 10px",
                                                backgroundColor: "rgba(255,255,255,0.06)",
                                                color: accent,
                                                fontSize: 12,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {publicPhotoSlides.length}
                                        </span>
                                    </div>

                                    <div
                                        className="kbs-public-photo-grid"
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))",
                                            gap: 10,
                                        }}
                                    >
                                        {publicPhotoSlides.map((photo, photoIndex) => {
                                            return (
                                                <button
                                                    key={photo.id}
                                                    type="button"
                                                    className="kbs-public-photo-thumb"
                                                    onClick={() => setSelectedPhotoIndex(photoIndex)}
                                                    style={{
                                                        position: "relative",
                                                        border: "1px solid rgba(255,255,255,0.08)",
                                                        borderRadius: 14,
                                                        overflow: "hidden",
                                                        padding: 0,
                                                        minHeight: 118,
                                                        background: "rgba(255,255,255,0.04)",
                                                        cursor: "pointer",
                                                    }}
                                                    aria-label={photo.caption || t("publicEventPage.photos.open")}
                                                >
                                                    <img
                                                        src={photo.thumbnailUrl}
                                                        alt={photo.caption || t("publicEventPage.photos.photoAlt")}
                                                        loading="lazy"
                                                        style={{
                                                            width: "100%",
                                                            height: 132,
                                                            objectFit: "cover",
                                                            display: "block",
                                                        }}
                                                    />
                                                    {photo.caption && (
                                                        <div
                                                            style={{
                                                                position: "absolute",
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                padding: "18px 8px 7px",
                                                                background:
                                                                    "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.78) 100%)",
                                                                color: textMain,
                                                                fontSize: 12,
                                                                textAlign: "left",
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {photo.caption}
                                                        </div>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </section>
                            )}
                        </div>

                        <div className="col-12 col-lg-3 order-1 order-lg-1">
                            <h3
                                className="kbs-section-title mb-3"
                                style={{
                                    fontSize: "1rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.15em",
                                    color: textMuted,
                                }}
                            >
                                {t("publicEventPage.sections.eventOverview")}
                            </h3>

                            <aside className="kbs-event-sidebar">
                                <section
                                    className="kbs-card kbs-card--details mb-4"
                                    style={{
                                        backgroundColor: surface,
                                        borderRadius: 18,
                                        padding: "18px 18px 20px",
                                        boxShadow: "0 20px 50px rgba(0,0,0,0.7)",
                                    }}
                                >
                                    <ul
                                        className="kbs-detail-list list-unstyled mb-3"
                                        style={{ fontSize: 13, color: textMain, lineHeight: 1.7 }}
                                    >
                                        {eventDate && (
                                            <DetailRow
                                                icon="far fa-clock"
                                                label={t("publicEventPage.labels.dateTime")}
                                                value={eventDate}
                                                textMuted={textMuted}
                                            />
                                        )}

                                        {address && (
                                            <DetailRow
                                                icon="fas fa-map-marker-alt"
                                                label={t("publicEventPage.labels.venue")}
                                                value={address}
                                                textMuted={textMuted}
                                            />
                                        )}

                                        {competitionLine && (
                                            <DetailRow
                                                icon="far fa-flag"
                                                label={t("publicEventPage.labels.competition")}
                                                value={competitionLine}
                                                textMuted={textMuted}
                                            />
                                        )}

                                        {event.eventType && (
                                            <DetailRow
                                                icon="fas fa-info-circle"
                                                label={t("publicEventPage.labels.eventType")}
                                                value={event.eventType}
                                                textMuted={textMuted}
                                            />
                                        )}
                                    </ul>

                                    {latitude && longitude && (
                                        <div className="kbs-map-block mt-3">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        color: textMuted,
                                                    }}
                                                >
                                                    {t("publicEventPage.labels.locationMap")}
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color: textMuted,
                                                        opacity: 0.9,
                                                    }}
                                                >
                                                    {t("publicEventPage.labels.googleMaps")}
                                                </span>
                                            </div>
                                            <div
                                                className="kbs-map-frame"
                                                style={{
                                                    borderRadius: 12,
                                                    overflow: "hidden",
                                                    border: "1px solid rgba(255,255,255,0.06)",
                                                }}
                                            >
                                                <iframe
                                                    title="Event Map"
                                                    width="100%"
                                                    height="220"
                                                    frameBorder="0"
                                                    style={{ border: 0 }}
                                                    src={`https://www.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`}
                                                    allowFullScreen
                                                />
                                            </div>
                                        </div>
                                    )}
                                </section>

                                <EventPeopleCard
                                    people={eventManagers}
                                    title={t("event.new.form.managers")}
                                    IconComponent={MdGroup}
                                    accent={accent}
                                    textMain={textMain}
                                    textMuted={textMuted}
                                    surface={surface}
                                />

                                <EventPeopleCard
                                    people={eventResponsibles}
                                    title={t("event.new.form.responsible")}
                                    IconComponent={GiWhistle}
                                    accent={accent}
                                    textMain={textMain}
                                    textMuted={textMuted}
                                    surface={surface}
                                />
                            </aside>
                        </div>

                        {streamingEnabled ? (
                            <div className="col-12 col-lg-3 order-3 order-lg-3">
                                <GameVideoPlayer
                                    videos={gameBroadcasts}
                                    game={event?.game}
                                    latestGameEvent={latestGameEvent}
                                    eventTitle={eventTitle}
                                    accent={accent}
                                    t={t}
                                    language={language}
                                />
                            </div>
                        ) : null}
                    </div>

                    <DownloadApp />
                </div>
            </main>

            {selectedPhotoIndex !== null && publicPhotoSlides.length > 0 && (
                <PublicPhotoPreview
                    photos={publicPhotoSlides}
                    currentIndex={selectedPhotoIndex}
                    onChange={setSelectedPhotoIndex}
                    onClose={() => setSelectedPhotoIndex(null)}
                    textMain={textMain}
                    textMuted={textMuted}
                    surface={surface}
                    t={t}
                />
            )}

            <PublicFooter imgPath="/dist/img/" />
        </div>
    );
}

function PublicPhotoPreview({ photos, currentIndex, onChange, onClose, textMain, textMuted, surface, t }) {
    const normalizedIndex = photos[currentIndex] ? currentIndex : 0;
    const photo = photos[normalizedIndex];
    const hasSlides = photos.length > 1;

    const showPrevious = () => {
        if (!hasSlides) return;
        onChange((normalizedIndex - 1 + photos.length) % photos.length);
    };

    const showNext = () => {
        if (!hasSlides) return;
        onChange((normalizedIndex + 1) % photos.length);
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }

            if (event.key === "ArrowLeft") {
                showPrevious();
            }

            if (event.key === "ArrowRight") {
                showNext();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    });

    if (!photo) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            className="kbs-public-photo-preview"
            onClick={onClose}
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 1050,
                background: "rgba(0,0,0,0.86)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 18,
            }}
        >
            <div
                onClick={(event) => event.stopPropagation()}
                style={{
                    width: "min(960px, 100%)",
                    maxHeight: "92vh",
                    background: surface,
                    borderRadius: 18,
                    overflow: "hidden",
                    boxShadow: "0 24px 90px rgba(0,0,0,0.85)",
                }}
            >
                <div
                    className="d-flex justify-content-between align-items-center"
                    style={{
                        padding: "10px 14px",
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                    }}
                >
                    <div
                        style={{
                            color: textMuted,
                            fontSize: 12,
                            textTransform: "uppercase",
                            letterSpacing: "0.12em",
                        }}
                    >
                        {t("publicEventPage.sections.photos")}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("publicEventPage.photos.close")}
                        style={{
                            border: "none",
                            background: "transparent",
                            color: textMain,
                            fontSize: 24,
                            lineHeight: 1,
                            cursor: "pointer",
                        }}
                    >
                        ×
                    </button>
                </div>

                <div
                    style={{
                        position: "relative",
                        background: "#000",
                    }}
                >
                    <img
                        src={photo.imageUrl}
                        alt={photo.caption || t("publicEventPage.photos.photoAlt")}
                        style={{
                            width: "100%",
                            maxHeight: "74vh",
                            objectFit: "contain",
                            display: "block",
                            background: "#000",
                        }}
                    />

                    {hasSlides && (
                        <>
                            <button
                                type="button"
                                onClick={showPrevious}
                                aria-label={t("publicEventPage.photos.previous")}
                                style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: 12,
                                    transform: "translateY(-50%)",
                                    width: 42,
                                    height: 42,
                                    borderRadius: 999,
                                    border: "1px solid rgba(255,255,255,0.28)",
                                    background: "rgba(0,0,0,0.52)",
                                    color: textMain,
                                    fontSize: 24,
                                    lineHeight: 1,
                                    cursor: "pointer",
                                }}
                            >
                                ‹
                            </button>

                            <button
                                type="button"
                                onClick={showNext}
                                aria-label={t("publicEventPage.photos.next")}
                                style={{
                                    position: "absolute",
                                    top: "50%",
                                    right: 12,
                                    transform: "translateY(-50%)",
                                    width: 42,
                                    height: 42,
                                    borderRadius: 999,
                                    border: "1px solid rgba(255,255,255,0.28)",
                                    background: "rgba(0,0,0,0.52)",
                                    color: textMain,
                                    fontSize: 24,
                                    lineHeight: 1,
                                    cursor: "pointer",
                                }}
                            >
                                ›
                            </button>
                        </>
                    )}
                </div>

                {(photo.caption || hasSlides) && (
                    <div
                        className="d-flex justify-content-between align-items-center"
                        style={{ padding: "10px 14px", color: textMain, fontSize: 13, gap: 12 }}
                    >
                        <span>{photo.caption}</span>
                        {hasSlides && (
                            <span style={{ color: textMuted, whiteSpace: "nowrap", fontSize: 12 }}>
                                {normalizedIndex + 1} / {photos.length}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function DetailRow({ icon, label, value, textMuted }) {
    return (
        <li
            className="kbs-detail-row d-flex align-items-start mb-2"
            style={{ columnGap: 12 }}
        >
            <div
                className="kbs-detail-icon d-flex align-items-center justify-content-center"
                style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.04)",
                    color: textMuted,
                    flexShrink: 0,
                }}
            >
                <i className={icon} style={{ fontSize: 13, color: accent }} />
            </div>
            <div className="kbs-detail-text flex-grow-1">
                <div
                    className="kbs-detail-label"
                    style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        color: textMuted,
                        marginBottom: 2,
                    }}
                >
                    {label}
                </div>
                <div className="kbs-detail-value" style={{ fontSize: 13 }}>
                    {value}
                </div>
            </div>
        </li>
    );
}

function EventPeopleCard({
    people,
    title,
    IconComponent,
    accent,
    textMain,
    textMuted,
    surface,
}) {
    const [expanded, setExpanded] = useState(people.length <= 2);

    if (!people.length) return null;

    return (
        <section
            className="kbs-card kbs-card--event-people mb-4"
            style={{
                backgroundColor: surface,
                borderRadius: 18,
                padding: "14px 16px",
                border: "1px solid rgba(255,255,255,0.06)",
                boxShadow: "0 18px 42px rgba(0,0,0,0.55)",
            }}
        >
            <button
                type="button"
                className="d-flex justify-content-between align-items-center w-100"
                onClick={() => setExpanded((current) => !current)}
                style={{
                    border: "none",
                    background: "transparent",
                    color: textMain,
                    padding: 0,
                    cursor: "pointer",
                    textAlign: "left",
                }}
            >
                <span className="d-flex align-items-center" style={{ gap: 10 }}>
                    <span
                        className="d-flex align-items-center justify-content-center"
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: "rgba(255,255,255,0.06)",
                            color: accent,
                        }}
                    >
                        <IconComponent size={18} />
                    </span>
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                        }}
                    >
                        {title}
                    </span>
                </span>

                <span className="d-flex align-items-center" style={{ gap: 8 }}>
                    <span
                        style={{
                            minWidth: 28,
                            height: 28,
                            borderRadius: 999,
                            padding: "5px 9px",
                            backgroundColor: accent,
                            color: pageBg,
                            fontSize: 12,
                            fontWeight: 800,
                            textAlign: "center",
                        }}
                    >
                        {people.length}
                    </span>
                    <i
                        className={expanded ? "fas fa-chevron-up" : "fas fa-chevron-down"}
                        style={{ color: textMuted, fontSize: 12 }}
                    />
                </span>
            </button>

            {expanded && (
                <div className="kbs-event-people-list mt-3">
                    {people.map((person) => (
                        <div
                            key={person.id}
                            className="d-flex align-items-center"
                            style={{
                                gap: 10,
                                padding: "10px 0",
                                borderTop: "1px solid rgba(255,255,255,0.06)",
                            }}
                        >
                            <InitialsAvatar
                                name={person.fullName}
                                size={36}
                                backgroundColor={accent}
                                textColor={pageBg}
                            />
                            <div
                                style={{
                                    color: textMain,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {person.fullName}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

function TimelineTabContent({ event, description, accent, textMain, textMuted, t }) {
    const game = event?.game;
    const homeTeam = game?.homeTeam;
    const visitorTeam = game?.visitorTeam;
    const gameId = game?.id ? parseInt(game.id, 10) : null;
    const queryTeamId = homeTeam?.id || visitorTeam?.id;
    const eventTypeStyle = useMemo(() => getTimelineEventConfig(t), [t]);

    const { data, loading, error } = useQuery(GET_SOCCER_GAME_TIMELINE, {
        variables: {
            gameId,
            teamId: parseInt(queryTeamId, 10),
        },
        skip: !gameId || !queryTeamId,
        fetchPolicy: "cache-and-network",
    });

    const timelineEvents = useMemo(() => {
        return (data?.soccerPlayerScoresByGame || [])
            .map((timelineEvent) => ({
                ...timelineEvent,
                side:
                    String(timelineEvent?.team?.id) === String(homeTeam?.id)
                        ? "left"
                        : "right",
            }))
            .sort((a, b) => {
                const minuteA = Number(a?.minute ?? 0);
                const minuteB = Number(b?.minute ?? 0);

                if (minuteB !== minuteA) return minuteB - minuteA;

                return Number(b?.id ?? 0) - Number(a?.id ?? 0);
            });
    }, [data, homeTeam?.id]);

    if (!game) {
        if (!description) {
            return (
                <div className="kbs-tab-empty text-center" style={{ color: textMuted }}>
                    <p className="mb-1" style={{ fontSize: 13 }}>
                        {t("publicEventPage.timeline.emptyTitle")}
                    </p>
                    <p className="mb-0" style={{ fontSize: 12 }}>
                        {t("publicEventPage.timeline.emptyDescription")}
                    </p>
                </div>
            );
        }

        return (
            <div className="kbs-timeline-content">
                <h5
                    className="mb-2"
                    style={{ color: accent, fontSize: "0.95rem", fontWeight: 600 }}
                >
                    {t("publicEventPage.labels.gameSummary")}
                </h5>
                <p
                    className="mb-0"
                    style={{
                        fontSize: "0.95rem",
                        color: textMain,
                        lineHeight: 1.7,
                    }}
                >
                    {description}
                </p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="kbs-tab-empty text-center" style={{ color: textMuted }}>
                <p className="mb-0" style={{ fontSize: 13 }}>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    {t("common.loading")}
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="kbs-tab-empty text-center" style={{ color: textMuted }}>
                <p className="mb-0" style={{ fontSize: 13 }}>
                    {getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}
                </p>
            </div>
        );
    }

    if (timelineEvents.length === 0) {
        return (
            <div className="kbs-tab-empty text-center" style={{ color: textMuted }}>
                <p className="mb-1" style={{ fontSize: 13 }}>
                    {t("publicEventPage.timeline.emptyTitle")}
                </p>
                <p className="mb-0" style={{ fontSize: 12 }}>
                    {t("publicEventPage.timeline.emptyDescription")}
                </p>
            </div>
        );
    }

    return (
        <div className="kbs-public-game-timeline">
            <div
                className="d-grid mb-3"
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 90px 1fr",
                    alignItems: "center",
                    gap: 8,
                    color: textMuted,
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                }}
            >
                <span>{homeTeam?.name || game?.homeTeamAlias || t("publicEventPage.labels.home")}</span>
                <span style={{ textAlign: "center", color: accent }}>
                    {t("publicEventPage.labels.timeline")}
                </span>
                <span style={{ textAlign: "right" }}>
                    {visitorTeam?.name || game?.visitorTeamAlias || t("publicEventPage.labels.away")}
                </span>
            </div>

            <div
                style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                }}
            >
                <div
                    className="kbs-public-timeline-rail"
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        left: "50%",
                        top: 0,
                        bottom: 0,
                        width: 2,
                        transform: "translateX(-50%)",
                        background:
                            "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.16), rgba(255,255,255,0.02))",
                    }}
                />

                {timelineEvents.map((timelineEvent) => {
                    const actionKey = String(timelineEvent?.actionType || "").toLowerCase();
                    const cfg = eventTypeStyle[actionKey] || {};
                    const side = timelineEvent.side === "right" ? "right" : "left";

                    return (
                        <div
                            className="kbs-public-timeline-row"
                            key={timelineEvent.id}
                            style={{
                                position: "relative",
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr) 74px minmax(0, 1fr)",
                                gap: 10,
                                alignItems: "center",
                            }}
                        >
                            <div
                                className="kbs-public-timeline-side kbs-public-timeline-side-left"
                                style={{ display: "flex", justifyContent: "flex-end" }}
                            >
                                {side === "left" && (
                                    <PublicTimelineEventCard
                                        event={timelineEvent}
                                        cfg={cfg}
                                        side={side}
                                        textMain={textMain}
                                        textMuted={textMuted}
                                        t={t}
                                    />
                                )}
                            </div>

                            <div
                                className="kbs-public-timeline-minute-wrap"
                                style={{
                                    position: "relative",
                                    zIndex: 2,
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <div
                                    style={{
                                        minWidth: 46,
                                        height: 46,
                                        borderRadius: 999,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: surface,
                                        color: textMain,
                                        fontSize: 12,
                                        fontWeight: 900,
                                        border: `2px solid ${cfg?.accent || accent}`,
                                        boxShadow: `0 0 0 5px ${surface}, 0 0 0 7px rgba(255,255,255,0.06)`,
                                    }}
                                >
                                    {timelineEvent?.minute}'
                                </div>
                            </div>

                            <div
                                className="kbs-public-timeline-side kbs-public-timeline-side-right"
                                style={{ display: "flex", justifyContent: "flex-start" }}
                            >
                                {side === "right" && (
                                    <PublicTimelineEventCard
                                        event={timelineEvent}
                                        cfg={cfg}
                                        side={side}
                                        textMain={textMain}
                                        textMuted={textMuted}
                                        t={t}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <style>{`
                @media (max-width: 767px) {
                    .kbs-public-game-timeline > .d-grid {
                        grid-template-columns: 1fr !important;
                        gap: 4px !important;
                    }
                    .kbs-public-game-timeline > .d-grid span {
                        text-align: left !important;
                    }
                    .kbs-public-timeline-rail {
                        left: 23px !important;
                        transform: none !important;
                    }
                    .kbs-public-timeline-row {
                        grid-template-columns: 54px minmax(0, 1fr) !important;
                        gap: 8px !important;
                    }
                    .kbs-public-timeline-minute-wrap {
                        grid-column: 1 !important;
                        grid-row: 1 !important;
                        justify-content: flex-start !important;
                    }
                    .kbs-public-timeline-side {
                        grid-column: 2;
                        grid-row: 1;
                        justify-content: flex-start !important;
                    }
                    .kbs-public-timeline-side:empty {
                        display: none !important;
                    }
                    .kbs-public-timeline-minute-wrap > div {
                        min-width: 42px !important;
                        height: 42px !important;
                    }
                    .kbs-public-timeline-card {
                        width: 100% !important;
                        max-width: none !important;
                    }
                }
            `}</style>
        </div>
    );
}

function PublicTimelineEventCard({ event, cfg, side, textMain, textMuted, t }) {
    const actionKey = String(event?.actionType || "").toLowerCase();
    const isSubstitution = actionKey === "substitution_out";
    const notes = String(event?.notes || "").trim();

    return (
        <div
            className="kbs-public-timeline-card"
            style={{
                width: "100%",
                maxWidth: 260,
                borderRadius: 16,
                padding: 12,
                border: `1px solid rgba(255,255,255,0.08)`,
                borderLeft:
                    side === "left"
                        ? `4px solid ${cfg?.accent || accent}`
                        : "1px solid rgba(255,255,255,0.08)",
                borderRight:
                    side === "right"
                        ? `4px solid ${cfg?.accent || accent}`
                        : "1px solid rgba(255,255,255,0.08)",
                background:
                    "linear-gradient(180deg, rgba(255,255,255,0.065), rgba(255,255,255,0.025))",
                color: textMain,
                boxShadow: "0 12px 28px rgba(0,0,0,0.28)",
            }}
        >
            <div
                className="d-flex justify-content-between align-items-start"
                style={{ gap: 10, marginBottom: 10 }}
            >
                <div style={{ minWidth: 0 }}>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 7,
                            color: cfg?.accent || accent,
                            fontSize: 12,
                            fontWeight: 900,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                        }}
                    >
                        <i className={cfg?.icon || "fas fa-circle"} />
                        <span>{cfg?.label || event?.actionType}</span>
                    </div>
                    <div
                        style={{
                            color: textMuted,
                            fontSize: 10,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                            marginTop: 4,
                        }}
                    >
                        {event?.team?.name || (side === "right" ? t("publicEventPage.labels.home") : t("publicEventPage.labels.away"))}
                    </div>
                </div>

                {cfg?.shortLabel && (
                    <span
                        style={{
                            borderRadius: 999,
                            padding: "3px 8px",
                            background: "rgba(255,255,255,0.08)",
                            color: cfg?.accent || accent,
                            fontSize: 10,
                            fontWeight: 900,
                            flexShrink: 0,
                        }}
                    >
                        {cfg.shortLabel}
                    </span>
                )}
            </div>

            {isSubstitution ? (
                <div style={{ display: "grid", gap: 8 }}>
                    <TimelinePerson
                        icon="fas fa-arrow-down"
                        color="#ff6b6b"
                        name={event?.player?.fullName}
                        label="Player out"
                        textMuted={textMuted}
                    />
                    <TimelinePerson
                        icon="fas fa-arrow-up"
                        color="#51cf66"
                        name={event?.relatedPlayer?.fullName}
                        label="Player in"
                        textMuted={textMuted}
                    />
                </div>
            ) : (
                <>
                    <div
                        style={{
                            fontSize: 14,
                            fontWeight: 900,
                            lineHeight: 1.25,
                            wordBreak: "break-word",
                        }}
                    >
                        {event?.player?.fullName || "-"}
                    </div>

                    {event?.relatedPlayer?.fullName && (
                        <div
                            style={{
                                marginTop: 6,
                                color: textMuted,
                                fontSize: 12,
                                fontWeight: 700,
                                wordBreak: "break-word",
                            }}
                        >
                            <i className="fas fa-link mr-2" />
                            {event.relatedPlayer.fullName}
                        </div>
                    )}
                </>
            )}

            {notes && (
                <div
                    style={{
                        marginTop: 10,
                        paddingTop: 9,
                        borderTop: "1px dashed rgba(255,255,255,0.12)",
                        color: textMuted,
                        fontSize: 12,
                        lineHeight: 1.45,
                    }}
                >
                    <i className="far fa-sticky-note mr-2" />
                    {notes}
                </div>
            )}
        </div>
    );
}

function TimelinePerson({ icon, color, name, label, textMuted }) {
    return (
        <div className="d-flex align-items-start" style={{ gap: 8 }}>
            <span style={{ color, width: 18, flexShrink: 0 }}>
                <i className={icon} />
            </span>
            <span style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 900, lineHeight: 1.25 }}>
                    {name || "-"}
                </div>
                <div
                    style={{
                        color: textMuted,
                        fontSize: 10,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginTop: 2,
                    }}
                >
                    {label}
                </div>
            </span>
        </div>
    );
}
function LineupsTabPlaceholder({ textMain, textMuted, t }) {
    return (
        <div className="kbs-lineups-placeholder text-center">
            <p className="mb-1" style={{ color: textMain, fontSize: 13 }}>
                {t("publicEventPage.lineups.title")}
            </p>
            <p className="mb-0" style={{ color: textMuted, fontSize: 12 }}>
                {t("publicEventPage.lineups.description")}
            </p>
        </div>
    );
}

function StatsTabPlaceholder({ textMain, textMuted, t }) {
    return (
        <div className="kbs-stats-placeholder text-center">
            <p className="mb-1" style={{ color: textMain, fontSize: 13 }}>
                {t("publicEventPage.stats.title")}
            </p>
            <p className="mb-0" style={{ color: textMuted, fontSize: 12 }}>
                {t("publicEventPage.stats.description")}
            </p>
        </div>
    );
}

function TeamBlock({ team, alias, label }) {
    const { t } = useTranslation();
    const { colors } = GlobalStyles;
    const displayName = team?.name || alias || t("publicEventPage.labels.teamFallback");

    const pageBg = colors?.woodsmoke || "#14151a";
    const surface = colors?.shark || "#1f1f2e";
    const accent = colors?.canary || "var(--kbs-primary, #00adff)";
    const textMuted = colors?.manatee || "#8d929d";

    return (
        <div className="kbs-team-block text-center" style={{ maxWidth: 220 }}>
            <div
                className="kbs-team-label mb-1"
                style={{
                    fontSize: 10,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: textMuted,
                }}
            >
                {label}
            </div>

            <div className="mb-2">
                {team?.logo ? (
                    <img
                        className="kbs-team-avatar"
                        width="72"
                        height="72"
                        src={`${HOST_URI}/media/${team.logo}`}
                        alt={`${displayName} Logo`}
                        style={{
                            borderRadius: "50%",
                            padding: 6,
                            background: `radial-gradient(circle, ${pageBg} 0, ${surface} 70%)`,
                        }}
                    />
                ) : (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <InitialsAvatar
                            name={displayName}
                            size={72}
                            backgroundColor={accent}
                            textColor={colors?.kBolaBlue || "var(--kbs-text-on-primary, #14151a)"}
                        />
                    </div>
                )}
            </div>

            <div
                className="kbs-team-name"
                style={{
                    color: accent,
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
                title={displayName}
            >
                {displayName}
            </div>
        </div>
    );
}

function MobileTeamRow({ team, alias, label, score }) {
    const { t } = useTranslation();
    const { colors } = GlobalStyles;
    const displayName = team?.name || alias || t("publicEventPage.labels.teamFallback");

    const pageBg = colors?.woodsmoke || "#14151a";
    const surface = colors?.shark || "#1f1f2e";
    const accent = colors?.canary || "var(--kbs-primary, #00adff)";
    const textMuted = colors?.manatee || "#8d929d";
    const textMain = colors?.white || "#ffffff";

    return (
        <div
            className="kbs-team-row-mobile d-flex align-items-center"
            style={{
                padding: "8px 10px",
                borderRadius: 12,
                backgroundColor: "rgba(0,0,0,0.35)",
            }}
        >
            <div
                className="kbs-team-row-avatar d-flex align-items-center justify-content-center"
                style={{ marginRight: 10 }}
            >
                {team?.logo ? (
                    <img
                        width="52"
                        height="52"
                        src={`${HOST_URI}/media/${team.logo}`}
                        alt={`${displayName} Logo`}
                        style={{
                            borderRadius: "50%",
                            padding: 4,
                            background: `radial-gradient(circle, ${pageBg} 0, ${surface} 70%)`,
                        }}
                    />
                ) : (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <InitialsAvatar
                            name={displayName}
                            size={52}
                            backgroundColor={accent}
                            textColor={colors?.kBolaBlue || "var(--kbs-text-on-primary, #14151a)"}
                        />
                    </div>
                )}
            </div>

            <div className="flex-grow-1">
                <div
                    style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.18em",
                        color: textMuted,
                        marginBottom: 2,
                    }}
                >
                    {label}
                </div>
                <div
                    style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: accent,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                    title={displayName}
                >
                    {displayName}
                </div>
            </div>

            <div
                className="kbs-team-row-score"
                style={{
                    marginLeft: 12,
                    fontSize: 16,
                    fontWeight: 700,
                    color: textMain,
                    minWidth: 24,
                    textAlign: "right",
                }}
            >
                {score}
            </div>
        </div>
    );
}

export default PublicEventPage;
